import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../ui/canon/Button.jsx';
import { PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { suggestedTopics as defaultSuggestedTopics } from '../../assistant/data/assistantData';

// Compact Ask card for LaunchPad: input + submit → navigates to /assistant?prompt=
// fitViewport: when true, constrains height to fit viewport with input always visible
export default function AssistantQuickAsk({ companyBrandedName, suggestedTopics = defaultSuggestedTopics, fitViewport = false }) {
  const intl = useIntl();
  const [q, setQ] = useState('');
  const to = q.trim() ? `/assistant?prompt=${encodeURIComponent(q.trim())}` : undefined;

  const brand = companyBrandedName || (typeof document !== 'undefined' && document.store?.getState?.()?.company?.branded_name) || 'the Assistant';
  const placeholder = intl.formatMessage({ id: 'assistant.modal.input_placeholder', defaultMessage: 'Ask {brand} a question' }, { brand });
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (to) window.location.href = to;
    }
  };

  // Container classes differ based on fitViewport prop
  const containerClasses = fitViewport
    ? "bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border flex flex-col"
    : "bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border";

  // Inner content classes
  const innerClasses = fitViewport
    ? "px-4 py-5 sm:p-6 flex flex-col flex-1 min-h-0"
    : "px-4 py-5 sm:p-6 flex flex-col gap-4";

  return (
    <div className={containerClasses} style={fitViewport ? { height: 'calc(100vh - 180px)', maxHeight: '600px', minHeight: '400px' } : undefined}>
      <div className={innerClasses}>
        {/* Heading (match AssistantChat empty state style, without subcopy) */}
        <div className="text-center space-y-3 flex-shrink-0">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-succession-secondary/10">
            <SparklesIcon className="h-6 w-6 text-succession-secondary" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-succession-text-primary m-0 p-0">{intl.formatMessage({ id: 'assistant.quick_ask.heading' })}</h2>
        </div>

        {/* Suggestions (limit 4) styled like AssistantChat buttons) */}
        <div className={fitViewport ? "grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 overflow-y-auto mt-4 min-h-0" : "grid grid-cols-1 md:grid-cols-2 gap-3"}>
          {suggestedTopics.slice(0, 4).map((t) => {
            const translatedTitle = intl.formatMessage({ id: `assistant.topics.${t.id}.title`, defaultMessage: t.title });
            const href = `/assistant?prompt=${encodeURIComponent(translatedTitle)}`;
            return (
              <a
                key={t.id}
                href={href}
                className="p-4 text-left rounded-lg border border-succession-border bg-succession-bg-primary hover:bg-succession-bg-secondary transition-all duration-300 hover:shadow-succession-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring h-fit"
                title={translatedTitle}
              >
                <div className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded bg-succession-secondary/10 flex items-center justify-center mt-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-succession-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-succession-text-primary m-0 p-0">{translatedTitle}</p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Input row - always pinned at bottom */}
        <div className={fitViewport ? "flex items-center gap-2 flex-shrink-0 mt-4 pt-4 border-t border-succession-border" : "flex items-center gap-2"}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="flex-1 border border-succession-border rounded-lg px-3 py-2 bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-secondary focus:outline-none focus:ring-2 focus:ring-succession-focus-ring"
          />
          {to ? (
            <Button as="a" href={to} title="Ask" aria-label="Ask" className="shrink-0 h-12 w-12 p-0 rounded-full">
              <PaperAirplaneIcon className="h-6 w-6" />
            </Button>
          ) : (
            <Button disabled className="shrink-0 h-12 w-12 p-0 rounded-full">
              <PaperAirplaneIcon className="h-6 w-6" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


